import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, formatDateId } from '../api/client';
import { Alert, Card, Input, Spinner } from '../components/ui';
import { useT } from '../i18n/locale';
import type { TranslationKey } from '../i18n/locale';

interface TenantRow {
  id: string;
  name: string;
  status: string;
  created_at: string;
  deletion_scheduled_for: string | null;
  plan_code: string | null;
  subscription_status: string | null;
  active_users: number;
  owner_email: string | null;
  rows_used: number | null;
  rows_limit: number | null;
}

const STATUS_CLASS: Record<string, string> = {
  active: 'bg-emerald-600',
  trialing: 'bg-blue-600',
  past_due: 'bg-amber-500',
  suspended: 'bg-red-600',
  closing: 'bg-slate-600',
  pending: 'bg-slate-400',
  cancelled: 'bg-slate-400',
};

/**
 * A raw tenant status maps to a label key. A value outside this set (a status
 * the server added later) falls through to the raw string, so the badge is
 * always at least diagnosable rather than blank.
 */
export const TENANT_STATUS_KEY: Record<string, TranslationKey> = {
  active: 'tenantStatus.active',
  trialing: 'tenantStatus.trialing',
  past_due: 'tenantStatus.pastDue',
  suspended: 'tenantStatus.suspended',
  closing: 'tenantStatus.closing',
  pending: 'tenantStatus.pending',
  cancelled: 'tenantStatus.cancelled',
};

export default function TenantsScreen() {
  const t = useT();
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (q: string) => {
      setLoading(true);
      const params = new URLSearchParams();
      if (q.trim()) params.set('search', q.trim());
      api
        .get<{ tenants: TenantRow[] }>(`/platform/tenants?${params}`)
        .then((d) => {
          setRows(d.tenants);
          setError(null);
        })
        .catch((e) => setError(e instanceof ApiError ? e.message : t('admin.tenantsLoadFailed')))
        .finally(() => setLoading(false));
    },
    [t],
  );

  useEffect(() => {
    // Debounced, so typing does not fire a query per keystroke.
    const id = window.setTimeout(() => load(search), 250);
    return () => window.clearTimeout(id);
  }, [search, load]);

  return (
    <div className="mx-auto w-full max-w-7xl p-4">
      {error ? <Alert tone="error">{error}</Alert> : null}

      <Card className="mb-3 p-3">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('admin.searchTenants')}
        />
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <Spinner label={t('admin.loadingTenants')} />
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm font-bold text-slate-400">
            {t('admin.noTenants')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead className="border-b border-slate-200 bg-slate-100/60 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
                <tr>
                  <th className="px-3 py-2">{t('admin.colOrg')}</th>
                  <th className="px-3 py-2">{t('admin.colOwner')}</th>
                  <th className="px-3 py-2">{t('admin.colPlan')}</th>
                  <th className="px-3 py-2 text-center">{t('admin.colUsers')}</th>
                  <th className="px-3 py-2">{t('admin.colQuota')}</th>
                  <th className="px-3 py-2">{t('admin.colStatus')}</th>
                  <th className="px-3 py-2">{t('admin.colCreated')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const statusKey = TENANT_STATUS_KEY[row.status];
                  return (
                  <tr key={row.id} className="border-b border-slate-100 hover:bg-blue-50/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/admin/tenants/${row.id}`}
                        className="text-sm font-bold text-teal-700 no-underline hover:underline"
                      >
                        {row.name}
                      </Link>
                      {row.deletion_scheduled_for ? (
                        <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                          {t('admin.deletedOn', {
                            date: formatDateId(row.deletion_scheduled_for),
                          })}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{row.owner_email ?? '-'}</td>
                    <td className="px-3 py-2 text-sm text-slate-600">{row.plan_code ?? '-'}</td>
                    <td className="px-3 py-2 text-center text-sm text-slate-700">
                      {row.active_users}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">
                      {row.rows_limit === null
                        ? '-'
                        : `${row.rows_used ?? 0} / ${row.rows_limit}`}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold text-white ${STATUS_CLASS[row.status] ?? 'bg-slate-400'}`}
                      >
                        {statusKey ? t(statusKey) : row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-500">
                      {formatDateId(row.created_at)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
